import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import './AdminTurnos.css';

const AdminTurnos = () => {
    const [turnos, setTurnos] = useState([]);
    const [masajistas, setMasajistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtros
    const [agruparPor, setAgruparPor] = useState('dia');
    const [fechaFiltro, setFechaFiltro] = useState('');
    const [searchPaciente, setSearchPaciente] = useState('');

    // Vista de horarios vacantes
    const [masajistaSeleccionado, setMasajistaSeleccionado] = useState('');
    const [fechaVacantes, setFechaVacantes] = useState('');
    const [horarios, setHorarios] = useState([]);
    const [loadingVacantes, setLoadingVacantes] = useState(false);
    const [masajistaInfo, setMasajistaInfo] = useState(null);

    // CARGA INICIAL
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [turnosRes, masajistasRes] = await Promise.all([
                    api.get('/admin/turnos-panel/turnos'),
                    api.get('/admin/turnos-panel/masajistas'),
                ]);
                setTurnos(turnosRes.data);
                setMasajistas(masajistasRes.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar datos');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const consultarVacantes = async () => {
        if (!masajistaSeleccionado || !fechaVacantes) {
            alert('Seleccioná un masajista y una fecha');
            return;
        }
        setLoadingVacantes(true);
        try {
            const res = await api.get(
                `/admin/turnos-panel/horarios-vacantes?masajistaId=${masajistaSeleccionado}&fecha=${fechaVacantes}`
            );
            setHorarios(res.data.horarios);
            setMasajistaInfo(res.data.masajista);
        } catch (err) {
            console.error(err);
            alert('Error al consultar horarios');
        } finally {
            setLoadingVacantes(false);
        }
    };

    const turnosFiltrados = useMemo(() => {
        let resultado = [...turnos];

        if (fechaFiltro) {
            resultado = resultado.filter(t => {
                // Convertimos la fecha UTC a hora local de Argentina para comparar
                const fechaTurno = new Date(new Date(t.fecha).getTime() - (3 * 60 * 60 * 1000)).toISOString().slice(0, 10);
                return fechaTurno === fechaFiltro;
            });
        }

        if (searchPaciente.trim()) {
            const term = searchPaciente.toLowerCase();
            resultado = resultado.filter(t => {
                const nombre = `${t.paciente?.nombre || ''} ${t.paciente?.apellido || ''}`.toLowerCase();
                const email = (t.paciente?.email || '').toLowerCase();
                return nombre.includes(term) || email.includes(term);
            });
        }

        return resultado;
    }, [turnos, fechaFiltro, searchPaciente]);

    // AGRUPAMIENTO Y ORDENAMIENTO
    const grupos = useMemo(() => {
        const mapa = new Map();

        turnosFiltrados.forEach(t => {
            let clave;
            let etiqueta;
            let timestampParaOrdenar = new Date(t.fecha).getTime();

            if (agruparPor === 'dia') {
                // Ajuste de zona horaria para agrupar correctamente
                const d = new Date(timestampParaOrdenar - (3 * 60 * 60 * 1000));
                clave = d.toISOString().slice(0, 10);
                etiqueta = d.toLocaleDateString('es-AR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    timeZone: 'UTC'
                });
            } else if (agruparPor === 'paciente') {
                clave = t.paciente?._id || 'sin-paciente';
                etiqueta = t.paciente
                    ? `${t.paciente.nombre || ''} ${t.paciente.apellido || ''}`.trim() || t.paciente.email
                    : 'Sin paciente';
            } else {
                clave = t.masajista?._id || 'sin-masajista';
                etiqueta = t.masajista
                    ? `${t.masajista.nombre || ''} ${t.masajista.apellido || ''}`.trim() || t.masajista.email
                    : 'Sin masajista';
            }

            if (!mapa.has(clave)) {
                mapa.set(clave, { etiqueta, turnos: [], timestamp: timestampParaOrdenar });
            }
            mapa.get(clave).turnos.push(t);
            
            // Guardamos el timestamp más reciente del grupo para ordenar los grupos
            if (timestampParaOrdenar > mapa.get(clave).timestamp) {
                mapa.get(clave).timestamp = timestampParaOrdenar;
            }
        });

        // 1. Ordenamos los turnos DENTRO de cada grupo (del más reciente al más viejo)
        mapa.forEach(grupo => {
            grupo.turnos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        });

        // 2. Ordenamos los GRUPOS (del más reciente al más viejo)
        return Array.from(mapa.entries())
            .sort(([claveA, valorA], [claveB, valorB]) => {
                // Si estamos agrupando por día, ordenamos por la fecha del grupo
                if (agruparPor === 'dia') {
                    return claveB.localeCompare(claveA); // Orden descendente
                }
                // Si agrupamos por paciente/masajista, ordenamos por el turno más reciente de ese grupo
                return valorB.timestamp - valorA.timestamp;
            })
            .map(([clave, valor]) => ({ clave, ...valor }));
    }, [turnosFiltrados, agruparPor]);

    const formatearHora = (fecha) => new Date(fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    const formatearFecha = (fecha) => new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    const getEstadoBadge = (estado) => {
        const map = { pendiente: 'pending', confirmado: 'confirmed', cancelado: 'cancelled', finalizado: 'finished' };
        return <span className={`turno-badge estado-${map[estado] || 'pending'}`}>{estado}</span>;
    };

    const getPagoBadge = (turno) => {
        if (turno.pagoEstado === 'aprobado') return <span className="turno-badge pago-pagado">💰 Seña pagada</span>;
        if (turno.pagoEstado === 'pendiente' || turno.estado === 'pendiente') return <span className="turno-badge pago-pendiente">⏳ Pago pendiente</span>;
        if (turno.estado === 'cancelado') return <span className="turno-badge pago-cancelado">❌ Cancelado</span>;
        return <span className="turno-badge pago-pendiente">Sin datos</span>;
    };

    if (loading) return <div className="admin-turnos"><div className="admin-turnos-loading">Cargando turnos...</div></div>;

    return (
        <div className="admin-turnos">
            <div className="admin-page-header" style={{ marginBottom: '1.5rem' }}>
                <h1>📅 Panel de Turnos</h1>
                <p className="admin-page-subtitle">Vista global de turnos y disponibilidad</p>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* Consulta de horarios vacantes */}
            <div className="admin-turnos-vacantes">
                <h3>🔍 Consultar disponibilidad</h3>
                <div className="vacantes-controls">
                    <select className="form-control" value={masajistaSeleccionado} onChange={(e) => setMasajistaSeleccionado(e.target.value)}>
                        <option value="">-- Elegí un masajista --</option>
                        {masajistas.map(m => <option key={m._id} value={m._id}>{m.nombre} {m.apellido}</option>)}
                    </select>
                    <input type="date" className="form-control" value={fechaVacantes} onChange={(e) => setFechaVacantes(e.target.value)} />
                    <button className="btn btn-primary" onClick={consultarVacantes} disabled={loadingVacantes}>
                        {loadingVacantes ? 'Consultando...' : 'Ver horarios'}
                    </button>
                </div>
                {horarios.length > 0 && masajistaInfo && (
                    <div className="vacantes-resultado">
                        <h4>Disponibilidad de {masajistaInfo.nombre} - {new Date(fechaVacantes + 'T12:00:00').toLocaleDateString('es-AR')}</h4>
                        <div className="horarios-grid">
                            {horarios.map(h => (
                                <div key={h.hora} className={`horario-slot ${h.ocupado ? 'ocupado' : 'libre'}`}>
                                    <div className="horario-hora">{h.hora}</div>
                                    <div className="horario-estado">{h.ocupado ? (h.estado === 'confirmado' ? '✅ Confirmado' : '🔒 Ocupado') : '🟢 Libre'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Filtros */}
            <div className="admin-turnos-filtros">
                <div className="filtros-row">
                    <div className="filtro-grupo">
                        <label>Agrupar por:</label>
                        <div className="toggle-group">
                            <button className={`toggle-btn ${agruparPor === 'dia' ? 'active' : ''}`} onClick={() => setAgruparPor('dia')}>📅 Día</button>
                            <button className={`toggle-btn ${agruparPor === 'paciente' ? 'active' : ''}`} onClick={() => setAgruparPor('paciente')}>👤 Paciente</button>
                            <button className={`toggle-btn ${agruparPor === 'profesional' ? 'active' : ''}`} onClick={() => setAgruparPor('profesional')}>🧘 Profesional</button>
                        </div>
                    </div>
                    <div className="filtro-grupo">
                        <label>Filtrar por fecha:</label>
                        <input type="date" className="form-control" value={fechaFiltro} onChange={(e) => setFechaFiltro(e.target.value)} />
                        {fechaFiltro && <button className="btn-clear" onClick={() => setFechaFiltro('')}>✖</button>}
                    </div>
                    <div className="filtro-grupo filtro-search">
                        <label>Buscar paciente:</label>
                        <input type="text" className="form-control" placeholder="Nombre o email..." value={searchPaciente} onChange={(e) => setSearchPaciente(e.target.value)} />
                    </div>
                </div>
            </div>

            {/* Lista de turnos (SIN las tarjetas de arriba) */}
            <div className="admin-turnos-lista">
                {grupos.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📭</div>
                        <div className="empty-state-title">No hay turnos</div>
                        <div className="empty-state-text">No se encontraron turnos con los filtros aplicados</div>
                    </div>
                ) : (
                    grupos.map(grupo => (
                        <div key={grupo.clave} className="grupo-turnos">
                            <div className="grupo-header">
                                <span className="grupo-etiqueta">
                                    {agruparPor === 'dia' && '📅 '} {agruparPor === 'paciente' && '👤 '} {agruparPor === 'profesional' && '🧘 '} {grupo.etiqueta}
                                </span>
                                <span className="grupo-count">{grupo.turnos.length} turno{grupo.turnos.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="grupo-tabla">
                                {grupo.turnos.map(turno => (
                                    <div key={turno._id} className="turno-row">
                                        <div className="turno-col turno-col-hora">
                                            <span className="turno-hora">{formatearHora(turno.fecha)}</span>
                                            <span className="turno-fecha">{formatearFecha(turno.fecha)}</span>
                                        </div>
                                        <div className="turno-col turno-col-paciente">
                                            <div className="turno-label">Paciente</div>
                                            <div className="turno-valor">{turno.paciente ? `${turno.paciente.nombre || ''} ${turno.paciente.apellido || ''}`.trim() : 'Sin paciente'}</div>
                                        </div>
                                        <div className="turno-col turno-col-masajista">
                                            <div className="turno-label">Profesional</div>
                                            <div className="turno-valor">{turno.masajista ? `${turno.masajista.nombre || ''} ${turno.masajista.apellido || ''}`.trim() : 'Sin masajista'}</div>
                                        </div>
                                        <div className="turno-col turno-col-estado">{getEstadoBadge(turno.estado)}</div>
                                        <div className="turno-col turno-col-pago">{getPagoBadge(turno)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AdminTurnos;